import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { SERVICES } from "../data/services";
import { getIcon } from "../lib/icons";
import LogoMark from "./LogoMark";
import ThemeToggle from "./ThemeToggle";

const NAV_LINKS = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services", dropdown: SERVICES },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
];

const Logo = ({ onDark }) => (
  <Link to="/" className="flex items-center gap-2.5" aria-label="Triguna Solutions home">
    <LogoMark className="h-9 w-9 rounded-[10px] shadow-md shadow-primary/30" />
    <span
      className={`font-display text-lg font-bold tracking-tight ${
        onDark ? "text-white" : "text-foreground"
      }`}
    >
      Triguna<span className={onDark ? "text-amber-300" : "text-primary"}> Solutions</span>
    </span>
  </Link>
);

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  // Every page opens with a dark hero, so at the top of the page the
  // navbar floats over a dark background and uses light text.
  const onDark = !scrolled && !mobileOpen;

  const linkClass = (path) => {
    if (onDark) {
      return isActive(path)
        ? "text-white"
        : "text-white/75 hover:text-white";
    }
    return isActive(path) ? "text-primary" : "text-foreground/80 hover:text-foreground";
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        onDark
          ? "border-b border-transparent bg-transparent"
          : "border-b border-border bg-background/80 backdrop-blur-lg"
      }`}
    >
      <nav className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo onDark={onDark} />

        {/* Desktop links */}
        <div className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) =>
            link.dropdown ? (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  onClick={() => navigate(link.path)}
                  className={`flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${linkClass(
                    link.path
                  )}`}
                >
                  {link.name}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                  />
                </button>

                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 12 }}
                      transition={{ duration: 0.18 }}
                      className="absolute left-1/2 top-full z-50 w-[36rem] -translate-x-1/2 pt-3"
                    >
                      <div className="overflow-hidden rounded-2xl border border-border bg-popover shadow-2xl shadow-primary/10">
                        <div className="grid grid-cols-2 gap-1 p-3">
                          {link.dropdown.map((service) => {
                            const Icon = getIcon(service.icon);
                            return (
                              <Link
                                key={service.slug}
                                to={`/services/${service.slug}`}
                                className="group flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-muted"
                              >
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-gradient text-white shadow-sm shadow-primary/30 transition-transform group-hover:scale-105">
                                  <Icon className="h-5 w-5" />
                                </span>
                                <span className="min-w-0">
                                  <span className="block text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
                                    {service.name}
                                  </span>
                                  <span className="block truncate text-xs text-muted-foreground">
                                    {service.tagline}
                                  </span>
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                        <Link
                          to="/services"
                          className="group flex items-center justify-between border-t border-border bg-muted/40 px-5 py-3 text-sm font-semibold text-primary transition-colors hover:bg-muted"
                        >
                          View all services
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={link.name}
                to={link.path}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${linkClass(
                  link.path
                )}`}
              >
                {link.name}
              </Link>
            )
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <ThemeToggle className="hidden sm:inline-flex" onDark={onDark} />
          <button
            onClick={() => navigate("/contact")}
            className="hidden rounded-full bg-brand-gradient px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary/30 transition-transform hover:scale-105 lg:inline-flex"
          >
            Schedule Consultation
          </button>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen((o) => !o)}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-lg lg:hidden ${
              onDark ? "text-white" : "text-foreground"
            }`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-border bg-background lg:hidden"
          >
            <div className="space-y-1 px-4 py-4">
              {NAV_LINKS.map((link) =>
                link.dropdown ? (
                  <div key={link.name}>
                    <button
                      onClick={() => setMobileServicesOpen((o) => !o)}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-base font-medium text-foreground"
                    >
                      {link.name}
                      <ChevronDown
                        className={`h-5 w-5 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence>
                      {mobileServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden pl-3"
                        >
                          <Link
                            to="/services"
                            className="block rounded-lg px-3 py-2 text-sm font-medium text-primary"
                          >
                            All Services
                          </Link>
                          {link.dropdown.map((service) => (
                            <Link
                              key={service.slug}
                              to={`/services/${service.slug}`}
                              className="block rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
                            >
                              {service.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`block rounded-lg px-3 py-2.5 text-base font-medium ${
                      isActive(link.path) ? "text-primary" : "text-foreground"
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              )}

              <div className="flex items-center gap-3 pt-3">
                <ThemeToggle />
                <button
                  onClick={() => navigate("/contact")}
                  className="flex-1 rounded-full bg-brand-gradient px-5 py-3 text-sm font-semibold text-white shadow-md"
                >
                  Schedule Consultation
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
